/**
 * Who gets signed out, and when. (V19.13, audit 19.12 items A1, A3, A4, B8)
 *
 * ONLY A 401 MEANS "SIGNED OUT". Offline, a timeout, a 5xx, a 429 or a server
 * still waking up is the server not answering - the person is still signed
 * in, and is kept signed in while the app tries again. Opening the app offline
 * used to sign people out, and the sign-out cancelled the session on the
 * server too.
 *
 * Pure functions, with everything outside passed in, so they run under
 * node:test. AuthContext.jsx is the only caller.
 */

/** Did the server say this session is over? Only a 401 does. */
export const isSignedOutAnswer = (err) => err?.response?.status === 401;

/**
 * Does this client keep its refresh pass in storage and send it in the body?
 * Native only. On the web the pass is the httpOnly cookie; a body pass kept
 * there goes stale the moment the cookie moves on (Google and Facebook on the
 * web were signed out every second renewal).
 */
export const keepsBodyPass = (isNative) => !!isNative;

/** The body for /auth/refresh and /auth/logout. */
export const refreshBody = (isNative, stored) =>
  (keepsBodyPass(isNative) && stored ? { refresh_token: stored } : {});

/**
 * Load the signed-in person at app open.
 *   { state: 'ok', user }   - signed in
 *   { state: 'signed_out' } - /auth/me said 401 AND the refresh said 401
 *   { state: 'retry' }      - the server did not answer; still signed in
 */
export async function loadSignedInUser({ getMe, refreshAndGetMe }) {
  try {
    return { state: 'ok', user: await getMe() };
  } catch (e) {
    if (!isSignedOutAnswer(e)) return { state: 'retry' };
  }
  try {
    return { state: 'ok', user: await refreshAndGetMe() };
  } catch (e) {
    return isSignedOutAnswer(e) ? { state: 'signed_out' } : { state: 'retry' };
  }
}

/** 2s, 4s, 8s ... capped at 30s. */
export const meRetryDelay = (attempt) => Math.min(30000, 2000 * 2 ** Math.max(0, attempt));

async function currentSubscription(serviceWorker) {
  if (!serviceWorker || typeof serviceWorker.getRegistration !== 'function') return null;
  const reg = await serviceWorker.getRegistration();
  if (!reg || !reg.pushManager) return null;
  return (await reg.pushManager.getSubscription()) || null;
}

/**
 * On sign-out: this phone's push endpoint, so the server can drop it, and the
 * phone's own subscription is cancelled - otherwise the next person to sign
 * in here sees notifications "On" for pushes that no longer come.
 * Never throws; null when there is nothing to forget.
 */
export async function forgetThisPhonesPush(serviceWorker) {
  try {
    const sub = await currentSubscription(serviceWorker);
    if (!sub || !sub.endpoint) return null;
    try { await sub.unsubscribe(); } catch { /* the server copy still goes */ }
    return sub.endpoint;
  } catch {
    return null;
  }
}

const b64 = (buf) => btoa(String.fromCharCode.apply(null, new Uint8Array(buf)));

/**
 * The /push/subscribe body for this phone's existing subscription, or null.
 * Used after a password change: the server ends every phone's push, and this
 * phone, which stays signed in, registers itself again.
 */
export async function thisPhonesPushSubscription(serviceWorker, memberName) {
  try {
    const sub = await currentSubscription(serviceWorker);
    if (!sub || !sub.endpoint) return null;
    return {
      endpoint: sub.endpoint,
      keys: { p256dh: b64(sub.getKey('p256dh')), auth: b64(sub.getKey('auth')) },
      member_name: memberName || null,
    };
  } catch {
    return null;
  }
}
